import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import {
    accountIdsForFallbacks,
    appendAccountIds,
} from "../invoiceLedgerAccountIds";
import { AllocationTableOptions } from "../editAllocation/AllocationTable";
import { AccrualMatchTableCallbacks, MatchableItem } from "../types";
import { AccountListCell } from "./AccountListCell";
import { RemainingLedgerAccountCell } from "./RemainingLedgerAccountCell";

/**
 * Read-only cash-side account column for the default (workspace-matched)
 * match drawer view (PRO-12048). For invoices this is the unique income-side
 * accounts across item, tax, and discount lines. For bills it is the unique
 * expense accounts on the lines. A line with no account uses the default for
 * its purpose. One account shows its name; more than one shows the first
 * name and a +N chip. Resolve-difference rows use an editable picker instead.
 */
const ReadOnlyCashLedgerCell = memo(
    ({ row }: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const {
            cashAccountIds = [],
            missingIncomeFallbacks = [],
            entityId,
            ledgerEntityId,
        } = row.original;
        const accountsEntityId = ledgerEntityId ?? entityId;
        const showAccountNumbers = useShowAccountNumbers();
        const { accounts } = useAccounts({
            entityId: accountsEntityId || undefined,
        });
        const accountIds = useMemo(
            () =>
                appendAccountIds(
                    cashAccountIds,
                    accountIdsForFallbacks(accounts, missingIncomeFallbacks),
                ),
            [accounts, cashAccountIds, missingIncomeFallbacks],
        );

        return (
            <AccountListCell
                accountIds={accountIds}
                accounts={accounts}
                showAccountNumbers={showAccountNumbers}
            />
        );
    },
);
ReadOnlyCashLedgerCell.displayName = "ReadOnlyCashLedgerCell";

export const CashLedgerCell = memo(
    (props: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const meta = useViewTableMeta<
            AccrualMatchTableCallbacks,
            AllocationTableOptions
        >(props.table);
        if (meta.options?.section === "remaining") {
            return (
                <RemainingLedgerAccountCell
                    {...props}
                    ledgerBasis={AccountingLedgerBasis.cash}
                    accountId={props.row.original.cashAccountIds?.[0]}
                    onAccountChange={
                        meta.callbacks?.onRemainingCashAccountChange
                    }
                />
            );
        }
        return <ReadOnlyCashLedgerCell {...props} />;
    },
);
CashLedgerCell.displayName = "CashLedgerCell";
